import { useUser } from "@modules/auth/hooks/useUser"
import { BoxSlotTypeOf } from "@modules/boxSlot/boxSlotTypes"
import { useServicesContext } from "@modules/core/services/services.context"
import { convertItemProgramSessionsToEvent } from "@modules/programSession/programSessionTypes"
import { ItemProgram } from "@modules/programs/programTypes"
import { convertTrainingContentFromDto } from "@modules/training/services/trainingDto"
import { convertItemTrainingSessionsToEvent } from "@modules/training/trainingTypes"
import { useQuery } from "@tanstack/react-query"
import dayjs from "dayjs"
import { useObservable } from "micro-observables"
import { DefaultTheme, useTheme } from "styled-components"

export const useTrainingSessionsToday = () => {
  const { trainingApi } = useServicesContext()

  const { data: trainingToday, isLoading } = useQuery({
    queryKey: ["trainingToday"],
    queryFn: () => trainingApi.getTrainingContentDate(dayjs().toString()),
    staleTime: 5 * 60 * 1000, // 5 min
  })

  return {
    trainingToday: trainingToday
      ? trainingToday.map((trainingContent: any) => convertTrainingContentFromDto(trainingContent))
      : [],
    isLoading,
  }
}

export const useProgramSelected = (programId: string) => {
  const { planningService } = useServicesContext()
  const programs = useObservable(planningService.filteredProgramPlanning)
  return programs.includes(programId)
}
export const useAllProgramsSelected = (listPrograms: ItemProgram[]) => {
  const { planningService } = useServicesContext()
  const programs = useObservable(planningService.filteredProgramPlanning)
  return listPrograms.every((program) => programs.includes(program.id))
}

export const useProgramSessionEvents = (programId: string | undefined) => {
  const theme = useTheme()
  const user = useUser()
  const { planningService } = useServicesContext()
  const planningSessions = useObservable(planningService.planningSessions)
  const filteredSessions = planningSessions.filter((session) => session.programId === programId)

  return {
    isLoading: useObservable(planningService.isLoadingSessions),
    sessions: convertItemProgramSessionsToEvent(filteredSessions, user?.id ?? "", theme as DefaultTheme),
  }
}

export const usePlanningSessionEvents = (startDate?: string, endDate?: string) => {
  const user = useUser()
  const theme = useTheme()

  const { trainingApi, planningService } = useServicesContext()
  const selectedPrograms = useObservable(planningService.filteredProgramPlanning)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["trainingSessions", startDate, endDate],
    queryFn: () => {
      if (startDate && endDate) return trainingApi.getTrainingContents(startDate, endDate)
    },
    enabled: !!startDate && !!endDate,
  })

  return {
    data: data
      ? convertItemTrainingSessionsToEvent(
          data
            .map((trainingContent: any) => convertTrainingContentFromDto(trainingContent))
            .filter(
              (session) =>
                session.type === BoxSlotTypeOf || selectedPrograms.includes(session.programSession.programId || ""),
            ),
          user?.id ?? "",
          theme as DefaultTheme,
        )
      : [],
    isLoading,
    refetch,
    fetch,
    isLoadingFilteredPrograms: useObservable(planningService.isLoadingPrograms),
  }
}
